import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { tv } from 'tailwind-variants'
import { Copy, ThumbsDown, ThumbsUp, Tick } from 'components/icons'
import Tooltip from 'components/tooltip'

export interface ChatMessage {
  role: 'user' | 'agent'
  content: string
  thinking?: string
  publicId?: string
  vote?: number
}

const messageBubble = tv({
  slots: {
    bubble: 'text-sm break-words leading-[1.6]',
    voteBtn:
      'inline-flex items-center justify-center w-6 h-6 p-0 rounded-md bg-transparent border-0 shadow-none outline-none cursor-pointer text-muted transition-colors hover:bg-surface-sunken hover:text-body [&_svg]:w-3.5 [&_svg]:h-3.5',
    actionRow: 'flex items-center gap-0.5 mt-1.5 -ml-1',
    thinkingContent:
      'text-xs leading-relaxed text-muted bg-surface-sunken rounded-lg px-2.5 py-2 mb-1.5 whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto',
    thinkingToggle:
      'border-0 bg-transparent shadow-none outline-none p-0 cursor-pointer text-[11px] text-muted flex items-center gap-1 mb-1.5 transition-colors duration-150 ease-in-out hover:text-blue',
    thinkingToggleArrow: 'text-[9px] transition-transform duration-200 ease-in-out'
  },
  variants: {
    expanded: {
      true: {
        thinkingToggleArrow: 'rotate-90'
      }
    },
    isUser: {
      // Your messages sit in a soft bubble on the right; MortaAI's replies are plain text across the panel.
      true: {
        bubble: 'w-fit max-w-[85%] ml-auto py-2 px-3.5 rounded-2xl bg-surface-sunken text-body whitespace-pre-wrap',
        actionRow: 'justify-end'
      },
      false: {
        bubble: 'text-body chat-markdown'
      }
    }
  }
})

const { voteBtn, thinkingContent, thinkingToggle, thinkingToggleArrow } = messageBubble()

const MessageBubble: React.FC<{ message: ChatMessage; onVote?: (vote: number, feedback?: string) => void }> = ({
  message,
  onVote
}) => {
  const isUser = message.role === 'user'
  const { bubble, actionRow } = messageBubble({ isUser })
  const [thinkingExpanded, setThinkingExpanded] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  const handleVote = (vote: number) => {
    if (!onVote) return
    if (vote === -1) {
      setFeedbackOpen(true)
    }
    onVote(vote)
  }

  const handleFeedbackSubmit = () => {
    if (!onVote || !feedbackText.trim()) return
    onVote(message.vote ?? -1, feedbackText.trim())
    setFeedbackOpen(false)
  }

  return (
    <div className={`animate-slide-in ${isUser ? 'mb-4' : 'mb-6'}`}>
      {!isUser && message.thinking && (
        <>
          <span className={thinkingToggle()} onClick={() => setThinkingExpanded(!thinkingExpanded)}>
            <span className={thinkingToggleArrow({ expanded: thinkingExpanded })}>▸</span>
            {thinkingExpanded ? 'Hide thinking' : 'Show thinking'}
          </span>
          {thinkingExpanded && <div className={thinkingContent()}>{message.thinking}</div>}
        </>
      )}
      <div className={bubble({ isUser })}>
        {isUser ? (
          message.content
        ) : (
          <ReactMarkdown
            disallowedElements={['img']}
            components={{
              a: (props: any) => (
                <a {...props} rel="noreferrer noopener" target="_blank">
                  {props.children}
                </a>
              )
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>
      {/* Copy on every message; votes on MortaAI's saved replies, the chosen one staying highlighted. */}
      {message.content?.trim() && (
        <div className={actionRow()}>
          <Tooltip content={copied ? 'Copied!' : 'Copy'}>
            <button type="button" aria-label={copied ? 'Copied!' : 'Copy'} className={voteBtn()} onClick={handleCopy}>
              {copied ? <Tick /> : <Copy />}
            </button>
          </Tooltip>
          {!isUser && message.publicId && (
            <>
              <button
                type="button"
                aria-label="Helpful"
                aria-pressed={message.vote === 1}
                className={voteBtn({ class: message.vote === 1 ? 'text-blue [&_svg]:fill-current' : undefined })}
                onClick={() => handleVote(1)}
              >
                <ThumbsUp />
              </button>
              <button
                type="button"
                aria-label="Not helpful"
                aria-pressed={message.vote === -1}
                className={voteBtn({ class: message.vote === -1 ? 'text-blue [&_svg]:fill-current' : undefined })}
                onClick={() => handleVote(-1)}
              >
                <ThumbsDown />
              </button>
              {message.vote != null && !feedbackOpen && (
                <button
                  type="button"
                  className="ml-1.5 text-xs text-muted cursor-pointer underline border-0 bg-transparent p-0 shadow-none outline-none"
                  onClick={() => setFeedbackOpen(true)}
                >
                  Add feedback
                </button>
              )}
            </>
          )}
        </div>
      )}
      {feedbackOpen && (
        <div className="flex items-center gap-2 mt-1.5 max-w-[82%]">
          <input
            className="text-xs flex-1 outline-none py-1 px-2 rounded-md border border-[var(--color-divider)]"
            placeholder="What could be improved?"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleFeedbackSubmit()
            }}
          />
          <button
            className="text-xs cursor-pointer border-0 text-on-solid py-1 px-2.5 rounded-md bg-blue"
            onClick={handleFeedbackSubmit}
            disabled={!feedbackText.trim()}
          >
            Send
          </button>
        </div>
      )}
    </div>
  )
}

export default MessageBubble
